import { MouseEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SerializedError } from '@reduxjs/toolkit';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { RootState } from '../../app/store';
import {
  CreateTenantActionParams,
  createTenantAction,
  listTenantsAction,
  tenant_field_options,
} from './tenantSlice';
import { hasScope } from '../auth/authSlice';
import { route_path, push } from '../../app/route';

type TenantForm = Required<CreateTenantActionParams>;

const initialState: TenantForm = {
  name: '',
  title: '',
  description: '',
  without_postfix: false,
};

export function TenantCreatePage() {
  const dispatch = useAppDispatch();
  const canCreateWithoutPostfix = useAppSelector((state: RootState) => {
    return hasScope(state, 'auth-portal/user_tenants.create_without_postfix');
  });
  const [api_error, setApiError] = useState<SerializedError|undefined>();
  const { handleSubmit, register, watch, formState: { errors } } = useForm({
    defaultValues: initialState,
  });

  const handleCreate = async(draft: TenantForm) => {
    let result = await dispatch(createTenantAction(draft));
    if (createTenantAction.fulfilled.match(result)) {
      await dispatch(push(route_path.home));
      dispatch(listTenantsAction());
    } else if (createTenantAction.rejected.match(result)) {
      setApiError(result.error);
      const subscription = watch(() => {
        setApiError(undefined);
        subscription.unsubscribe();
      });
    }
  };
  const handleCancel = (event: MouseEvent) => {
    event.preventDefault();
    dispatch(push(route_path.home));
  };
  return (
    <div className="container">
      <div className="d-flex justify-content-between py-4">
        <h1>Create Tenant</h1>
      </div>
      <form onSubmit={handleSubmit(handleCreate)}>
        <fieldset>
          <div className="mb-3 row">
            <label htmlFor="tenant_title" className="col-sm-3 col-form-label">Title</label>
            <div className="col-sm-6">
              <input type="text" id="tenant_title" className="form-control"
                {...register('title', tenant_field_options.title)}
              />
            </div>
            <span className="col-sm-3 text-danger py-1">{errors.title?.message}</span>
          </div>
          <div className="mb-3 row">
            <label htmlFor="tenant_name" className="col-sm-3 col-form-label">Name</label>
            <div className="col-sm-6">
              <input type="text" id="tenant_name" className="form-control"
                {...register('name', tenant_field_options.name)}
              />
            </div>
            <span className="col-sm-3 text-danger py-1">{errors.name?.message}</span>
          </div>
          <div className="mb-3 row">
            <label htmlFor="tenant_description" className="col-sm-3 col-form-label">Description</label>
            <div className="col-sm-6">
              <textarea id="tenant_description" className="form-control"
                {...register('description', tenant_field_options.description)}
              />
            </div>
            <span className="col-sm-3 text-danger py-1">{errors.description?.message}</span>
          </div>
          {canCreateWithoutPostfix &&
          <div className="mb-3 row">
            <label htmlFor="without_postfix" className="col-sm-3 col-form-label">TenantId without postflix</label>
            <div className="mt-2 col-sm-6">
              <div className="col-sm-6">
                <input id="without_postfix" type="checkbox" className="form-check-input"
                  { ...register('without_postfix', tenant_field_options.without_postfix) }
                />
              </div>
            </div>
          </div>
          }
          <div className="d-flex">
            <span className="text-danger">{api_error?.message}</span>
          </div>
          <div className="d-flex my-3 justify-content-center">
            <button type="submit" className="btn btn-primary mx-3">Create</button>
            <button className="btn btn-light mx-3" onClick={(e) => handleCancel(e)}>Cancel</button>
          </div>
        </fieldset>
      </form>
    </div>
  );
}
